/**
 * Loyzen Auth — thin wrapper around Supabase Auth.
 * All password hashing, session management, and token handling
 * is delegated entirely to Supabase. No passwords are ever
 * stored, hashed, or verified by our own code.
 *
 * Store and subscription rows are created automatically by the
 * PostgreSQL trigger `handle_new_owner` (SECURITY DEFINER) when
 * a new auth.users row is inserted. The frontend NEVER inserts
 * into `subscriptions` directly — RLS blocks that intentionally.
 *
 * This file is the ONLY auth entry point for client components.
 * Server components use src/lib/supabase/server.ts directly.
 */

import { createClient } from '@/lib/supabase/client';
import type { User, Session } from '@supabase/supabase-js';
import { AuthResult } from '@/lib/auth';


export type { User, Session };

export interface OwnerProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
}

export interface StoreData {
  id: string;
  ownerId: string;
  businessName: string;
  country?: string;
  currency?: string;
  language?: string;
  programName?: string;
  pointsPerUnit?: number;
  rewardThreshold?: number;
  rewardValue?: number;
  rewardType?: string;
  vendorName?: string;
  createdAt: string;
  // Extended fields from migration 20260730060000
  sector?: string;
  logoUrl?: string;
  address?: string;
  phone?: string;
  website?: string;
  timezone?: string;
  dateFormat?: string;
  numberFormat?: string;
  defaultDialCode?: string;
  loyaltyActive?: boolean;
}

export interface SubscriptionData {
  id: string;
  storeId: string;
  ownerId: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired' | 'incomplete' | 'incomplete_expired' | 'unpaid';
  trialStartedAt: string;
  trialEndsAt: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  planInterval?: string;
}

export type SignupPayload = {
  firstName: string;
  email: string;
  password: string;
};

export type AuthError =
  | 'EMAIL_EXISTS' |'INVALID_CREDENTIALS' |'EMAIL_NOT_FOUND' |'EMAIL_NOT_CONFIRMED' |'SETUP_INCOMPLETE' |'UNKNOWN';

export type AuthResult<T = User> =
  | { ok: true; data: T }
  | { ok: false; code: AuthError; message: string };

// ─── Sign Up ──────────────────────────────────────────────────────────────────

/**
 * Creates a Supabase Auth user.
 * The DB trigger `handle_new_owner` (SECURITY DEFINER) automatically
 * creates owner_profile, store, and subscription rows server-side.
 * We then verify those rows exist before returning success.
 */
export async function signUp(payload: SignupPayload): Promise<AuthResult> {
  const supabase = createClient();
  const email = payload.email.trim().toLowerCase();

  const { data, error } = await supabase.auth.signUp({
    email,
    password: payload.password,
    options: {
      data: {
        first_name: payload.firstName.trim(),
      },
      emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL ?? ''}/auth/callback`,
    },
  });

  if (error) {
    if (
      error.message.toLowerCase().includes('already registered') ||
      error.message.toLowerCase().includes('already exists') ||
      error.message.toLowerCase().includes('user already')
    ) {
      return {
        ok: false,
        code: 'EMAIL_EXISTS',
        message:
          'Un compte existe déjà avec cette adresse. Connectez-vous ou réinitialisez votre mot de passe.',
      };
    }
    return { ok: false, code: 'UNKNOWN', message: error.message };
  }

  if (!data.user) {
    return {
      ok: false,
      code: 'UNKNOWN',
      message: 'Erreur lors de la création du compte. Veuillez réessayer.',
    };
  }

  // Verify that the DB trigger created the required rows.
  // Poll up to 5 seconds (trigger is near-instant but async relative to the JS call).
  const userId = data.user.id;
  let profileReady = false;
  let storeReady = false;
  let subscriptionReady = false;

  for (let attempt = 0; attempt < 5; attempt++) {
    await new Promise((r) => setTimeout(r, 1000));

    const [profileRes, storeRes] = await Promise.all([
      supabase.from('owner_profiles').select('id').eq('id', userId).maybeSingle(),
      supabase.from('stores').select('id').eq('owner_id', userId).maybeSingle(),
    ]);

    profileReady = !!profileRes.data;
    storeReady = !!storeRes.data;

    if (profileReady && storeReady) {
      const subRes = await supabase
        .from('subscriptions')
        .select('id')
        .eq('owner_id', userId)
        .maybeSingle();
      subscriptionReady = !!subRes.data;
      if (subscriptionReady) break;
    }
  }

  if (!profileReady || !storeReady || !subscriptionReady) {
    // Trigger did not complete in time — report the failure clearly.
    console.error('[Loyzen] Post-signup verification failed', {
      profileReady,
      storeReady,
      subscriptionReady,
    });
    return {
      ok: false,
      code: 'SETUP_INCOMPLETE',
      message:
        'Votre compte a été créé mais la configuration initiale a échoué. Veuillez contacter le support ou réessayer de vous connecter.',
    };
  }

  // ── Send welcome email (fire-and-forget, non-blocking) ──────────────────
  // Triggered once after successful account + store creation.
  // Idempotency is enforced server-side via welcome_emails_sent table.
  // A failure here NEVER blocks account creation.
  try {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? '';
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL ?? '';
    if (siteUrl && supabaseUrl) {
      const edgeFnUrl = `${supabaseUrl}/functions/v1/send-email`;
      fetch(edgeFnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Use the anon key for edge function calls from the client
          'Authorization': `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ?? ''}`,
        },
        body: JSON.stringify({
          type: 'welcome',
          to: email,
          locale: 'fr',
          customerName: payload.firstName.trim(),
          userId: userId,
        }),
      }).catch((err) => {
        console.warn('[Loyzen] Welcome email trigger failed (non-blocking):', err?.message);
      });
    }
  } catch {
    // Non-blocking — account creation succeeds regardless
  }

  return { ok: true, data: data.user };
}

// ─── Sign In ──────────────────────────────────────────────────────────────────

export async function signIn(email: string, password: string): Promise<AuthResult> {
  const supabase = createClient();

  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim().toLowerCase(),
    password,
  });

  if (error) {
    if (
      error.message.toLowerCase().includes('email not confirmed') ||
      error.message.toLowerCase().includes('not confirmed')
    ) {
      return {
        ok: false,
        code: 'EMAIL_NOT_CONFIRMED',
        message:
          "Votre adresse email n'a pas encore été confirmée. Vérifiez votre boîte mail.",
      };
    }
    return {
      ok: false,
      code: 'INVALID_CREDENTIALS',
      message:
        'Identifiants incorrects. Vérifiez votre adresse email et votre mot de passe.',
    };
  }

  if (!data.user) {
    return {
      ok: false,
      code: 'INVALID_CREDENTIALS',
      message:
        'Identifiants incorrects. Vérifiez votre adresse email et votre mot de passe.',
    };
  }

  return { ok: true, data: data.user };
}

// ─── Sign Out ─────────────────────────────────────────────────────────────────

export async function signOut(): Promise<void> {
  const supabase = createClient();
  await supabase.auth.signOut();
}

// ─── Get Current User ─────────────────────────────────────────────────────────

export async function getCurrentUser(): Promise<User | null> {
  const supabase = createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user;
}

// ─── Get Owner Profile ────────────────────────────────────────────────────────

export async function getOwnerProfile(userId: string): Promise<OwnerProfile | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('owner_profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error || !data) return null;

  return {
    id: data.id,
    email: data.email,
    firstName: data.first_name,
    lastName: data.last_name ?? '',
    createdAt: data.created_at,
  };
}

// ─── Get Store ────────────────────────────────────────────────────────────────

export async function getOwnerStore(userId: string): Promise<StoreData | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('stores')
    .select('*')
    .eq('owner_id', userId)
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    ownerId: data.owner_id,
    businessName: data.business_name,
    country: data.country,
    currency: data.currency,
    language: data.language,
    programName: data.program_name,
    pointsPerUnit: data.points_per_unit,
    rewardThreshold: data.reward_threshold,
    rewardValue: data.reward_value,
    rewardType: data.reward_type,
    vendorName: data.vendor_name,
    createdAt: data.created_at,
    // Extended fields
    sector: data.sector ?? undefined,
    logoUrl: data.logo_url ?? undefined,
    address: data.address ?? undefined,
    phone: data.phone ?? undefined,
    website: data.website ?? undefined,
    timezone: data.timezone ?? 'Europe/Paris',
    dateFormat: data.date_format ?? 'DD/MM/YYYY',
    numberFormat: data.number_format ?? 'fr-FR',
    defaultDialCode: data.default_dial_code ?? '+33',
    loyaltyActive: data.loyalty_active ?? true,
  };
}

// ─── Get Subscription ─────────────────────────────────────────────────────────

export async function getOwnerSubscription(userId: string): Promise<SubscriptionData | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('owner_id', userId)
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    storeId: data.store_id,
    ownerId: data.owner_id,
    status: data.status,
    trialStartedAt: data.trial_started_at,
    trialEndsAt: data.trial_ends_at,
    stripeCustomerId: data.stripe_customer_id,
    stripeSubscriptionId: data.stripe_subscription_id,
    currentPeriodStart: data.current_period_start,
    currentPeriodEnd: data.current_period_end,
    planInterval: data.plan_interval,
  };
}

// ─── Trial Days Remaining ─────────────────────────────────────────────────────

export function getTrialDaysRemaining(trialEndsAt: string): number {
  const end = new Date(trialEndsAt).getTime();
  const now = Date.now();
  const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

// ─── Password Reset ───────────────────────────────────────────────────────────

/**
 * Sends a real password reset email via Supabase.
 * The email contains a secure, time-limited link.
 * We NEVER display a reset code on screen.
 */
export async function requestPasswordReset(
  email: string
): Promise<{ ok: boolean; message: string }> {
  const supabase = createClient();

  const { error } = await supabase.auth.resetPasswordForEmail(
    email.trim().toLowerCase(),
    {
      redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL ?? ''}/auth/callback?next=/reset-password`,
    }
  );

  if (error) {
    console.error('[Loyzen] Password reset error:', error.message);
  }

  // Always return the same message for security (don't reveal if email exists)
  return {
    ok: true,
    message:
      'Si un compte existe avec cette adresse, un lien de réinitialisation a été envoyé.',
  };
}

/**
 * Updates the password for the currently authenticated user.
 * Called from the reset-password page after the user clicks the email link.
 */
export async function updatePassword(
  newPassword: string
): Promise<{ ok: boolean; message: string }> {
  const supabase = createClient();

  const { error } = await supabase.auth.updateUser({ password: newPassword });

  if (error) {
    return { ok: false, message: error.message };
  }

  return { ok: true, message: 'Mot de passe mis à jour avec succès.' };
}
